// MultiSelectPersonaFilter.tsx - Modern styled version
import {
  Box,
  Divider,
  Flex,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
  VStack,
} from '@chakra-ui/react';
import { IoSearch } from 'react-icons/io5';
import { MdOutlineSmartToy } from 'react-icons/md';
import { RiCheckLine } from 'react-icons/ri';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { HelivanColor } from '../helivan/HelivanColor';

// Define props interface
interface MultiSelectPersonaFilterProps {
  setSelectedTwins: (newSelection: Set<string>) => void;
  selectedTwinIds: Set<string>;
  allTwins: { id: string; name: string }[];
  maxSelections?: number;
  onClose?: () => void;
}

// Memoized search input component
const SearchInput = React.memo(
  ({
    value,
    onChange,
  }: {
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  }) => (
    <Box px={2} pb={2}>
      <InputGroup size="sm">
        <InputLeftElement pointerEvents="none">
          <IoSearch size={14} color={HelivanColor.text.blueMid} />
        </InputLeftElement>
        <Input
          placeholder="Search..."
          value={value}
          onChange={onChange}
          borderRadius="6px"
          bg={HelivanColor.background.secondary}
          border="none"
          fontSize="13px"
          _placeholder={{ color: HelivanColor.text.blueMid }}
          autoFocus
        />
      </InputGroup>
    </Box>
  )
);

// Memoized list item component
const PersonaItem = React.memo(
  ({
    persona,
    isSelected,
    onClick,
    disabled,
  }: {
    persona: { id: string; name: string };
    isSelected: boolean;
    onClick: () => void;
    disabled: boolean;
  }) => (
    <Flex
      width="full"
      justifyContent="space-between"
      alignItems="center"
      _hover={{
        cursor: disabled ? 'not-allowed' : 'pointer',
        bg: disabled ? 'white' : HelivanColor.background.secondary,
      }}
      onClick={disabled ? undefined : onClick}
      py={2}
      px={3}
      bg={isSelected ? HelivanColor.hover.lightBlue : 'white'}
      opacity={disabled ? 0.5 : 1}
    >
      <HStack spacing={2}>
        <MdOutlineSmartToy size={14} color={HelivanColor.text.blueMid} />
        <Text fontSize="13px" color={HelivanColor.text.blueDark} noOfLines={1}>
          {persona.name}
        </Text>
      </HStack>
      {isSelected && (
        <RiCheckLine size={14} color={HelivanColor.text.helivanBlue} />
      )}
    </Flex>
  )
);

function MultiSelectPersonaFilter({
  setSelectedTwins,
  selectedTwinIds,
  allTwins,
  maxSelections = 6,
  onClose,
}: MultiSelectPersonaFilterProps) {
  // Local state for search
  const [searchQuery, setSearchQuery] = useState('');

  // Ref for detecting outside clicks
  const menuRef = useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        // Click was outside the menu
        if (onClose) {
          onClose();
        }
      }
    }

    // Add event listener
    document.addEventListener('mousedown', handleClickOutside);

    // Clean up
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  // Get unique personas (by name)
  const uniquePersonas = useMemo(() => {
    const uniqueNames = new Set<string>();
    const personas: { id: string; name: string }[] = [];

    allTwins.forEach((twin) => {
      if (!uniqueNames.has(twin.name)) {
        uniqueNames.add(twin.name);
        personas.push(twin);
      }
    });

    return personas;
  }, [allTwins]);

  // Get filtered personas based on search query
  const filteredPersonas = useMemo(() => {
    if (!searchQuery) {
      return uniquePersonas;
    }

    const query = searchQuery.toLowerCase();
    return uniquePersonas.filter((p) => p.name.toLowerCase().includes(query));
  }, [uniquePersonas, searchQuery]);

  // Check if a persona ID is selected
  const isPersonaSelected = useCallback(
    (id: string): boolean => {
      return selectedTwinIds.has(id);
    },
    [selectedTwinIds]
  );

  // Sort personas (selected first, then alphabetical)
  const sortedPersonas = useMemo(() => {
    return [...filteredPersonas].sort((a, b) => {
      const aSelected = isPersonaSelected(a.id);
      const bSelected = isPersonaSelected(b.id);

      if (aSelected !== bSelected) {
        return aSelected ? -1 : 1;
      }

      return a.name.localeCompare(b.name);
    });
  }, [filteredPersonas, isPersonaSelected]);

  // Check if max selection limit reached
  const isSelectionLimitReached = selectedTwinIds.size >= maxSelections;

  // Handle toggling a persona selection - THIS IS THE CRITICAL FUNCTION
  const handleTogglePersona = useCallback(
    (id: string) => {
      // Create a completely new Set using spread for immutability
      const newSelection = new Set(Array.from(selectedTwinIds));

      if (newSelection.has(id)) {
        // If already selected, remove it
        newSelection.delete(id);
      } else if (newSelection.size < maxSelections) {
        // If not selected and under the limit, add it
        newSelection.add(id);
      } else {
        // At limit, can't add more
        return;
      }

      // Update parent component with the new selection
      setSelectedTwins(newSelection);
    },
    [selectedTwinIds, setSelectedTwins, maxSelections]
  );

  // Handle search input change
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value);
    },
    []
  );

  // Find divider index between selected and unselected items
  const dividerIndex = useMemo(() => {
    const index = sortedPersonas.findIndex((p) => !isPersonaSelected(p.id));
    return index > 0 && index < sortedPersonas.length ? index : -1;
  }, [sortedPersonas, isPersonaSelected]);

  // Handle keyboard events
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape' && onClose) {
        onClose();
      }
    },
    [onClose]
  );

  return (
    <Box
      ref={menuRef}
      position="fixed"
      width="250px"
      onKeyDown={handleKeyDown}
      boxShadow="0 4px 20px rgba(0,0,0,0.15)"
      borderRadius="8px"
      border="1px solid"
      borderColor={HelivanColor.border.primary}
      bg="white"
      py={2}
      zIndex={1400}
    >
      <SearchInput value={searchQuery} onChange={handleSearchChange} />
      <Box maxH="250px" overflowY="auto">
        {sortedPersonas.length === 0 ? (
          <Box px={3} py={2}>
            <Text fontSize="13px" color={HelivanColor.text.gray}>
              No results found
            </Text>
          </Box>
        ) : (
          <VStack align="stretch" spacing={0}>
            {sortedPersonas.map((persona, index) => {
              const isSelected = isPersonaSelected(persona.id);
              const isDisabled = !isSelected && isSelectionLimitReached;

              return (
                <React.Fragment key={persona.id}>
                  <PersonaItem
                    persona={persona}
                    isSelected={isSelected}
                    onClick={() => handleTogglePersona(persona.id)}
                    disabled={isDisabled}
                  />

                  {/* Add divider between selected and unselected */}
                  {dividerIndex > 0 && index === dividerIndex - 1 && (
                    <Divider borderColor={HelivanColor.border.primary} />
                  )}
                </React.Fragment>
              );
            })}
          </VStack>
        )}
      </Box>
      {isSelectionLimitReached && (
        <Box px={3} pt={2} borderTop="1px solid" borderTopColor={HelivanColor.border.primary}>
          <Text fontSize="11px" color={HelivanColor.text.blueMid}>
            Maximum {maxSelections} selections reached
          </Text>
        </Box>
      )}
    </Box>
  );
}

// Export component
export default React.memo(MultiSelectPersonaFilter);
